import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Upload, Loader2, PartyPopper } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { submitLead } from "@/lib/api";
import { WFH } from "@/constants/testIds";

const STEPS = [
  { title: "About you", fields: ["full_name", "mobile", "whatsapp", "email"] },
  { title: "Background", fields: ["city", "qualification", "skills"] },
  { title: "Finish up", fields: ["resume", "message"] },
];

const emptyForm = {
  full_name: "", mobile: "", whatsapp: "", email: "",
  city: "", qualification: "", skills: "", message: "",
};

const easeOut = [0.22, 1, 0.36, 1];

function validateStep(step, form) {
  const errs = {};
  if (step === 0) {
    if (!form.full_name.trim()) errs.full_name = "Full name is required";
    if (!/^[+\d][\d\s-]{7,}$/.test(form.mobile.trim())) errs.mobile = "Enter a valid mobile number";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) errs.email = "Enter a valid email";
  }
  if (step === 1) {
    if (!form.city.trim()) errs.city = "City is required";
    if (!form.qualification.trim()) errs.qualification = "Qualification is required";
  }
  return errs;
}

export default function ApplicationForm({ open, onOpenChange }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [resume, setResume] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => {
    setStep(0); setForm(emptyForm); setResume(null);
    setErrors({}); setDone(false); setServerError("");
  };

  const handleOpenChange = (v) => {
    if (!v) setTimeout(reset, 250);
    onOpenChange(v);
  };

  const next = () => {
    const errs = validateStep(step, form);
    setErrors(errs);
    if (Object.keys(errs).length === 0) setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setServerError("");
    setSubmitting(true);
    try {
      await submitLead("applications", form, resume);
      setDone(true);
    } catch (err) {
      setServerError(
        err?.response?.data?.detail || "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  const field = (name, label, props = {}) => (
    <div className="space-y-1.5">
      <Label htmlFor={`wfh-${name}`} className="text-sm font-medium text-neutral-300">
        {label}
      </Label>
      <Input
        id={`wfh-${name}`}
        data-testid={WFH.field(name)}
        value={form[name]}
        onChange={set(name)}
        className="h-11 rounded-xl border-white/10 bg-white/[0.04] transition-shadow focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1"
        {...props}
      />
      {errors[name] && (
        <p className="text-xs font-medium text-red-500">{errors[name]}</p>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid={WFH.dialog}
        className="max-w-lg overflow-hidden rounded-2xl p-0"
      >
        {!done ? (
          <div className="p-6 sm:p-8">
            <DialogHeader className="space-y-1 text-left">
              <DialogTitle className="font-display text-2xl font-black tracking-tight text-white">
                Apply for Work From Home
              </DialogTitle>
              <DialogDescription className="text-sm text-neutral-400">
                Step {step + 1} of {STEPS.length} — {STEPS[step].title}
              </DialogDescription>
            </DialogHeader>

            {/* progress */}
            <div className="mt-5 h-1.5 w-full overflow-hidden rounded-full bg-white/10">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-brand to-cyan-accent"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4, ease: easeOut }}
              />
            </div>

            <div className="mt-7 min-h-[248px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.35, ease: easeOut }}
                  className="space-y-4"
                >
                  {step === 0 && (
                    <>
                      {field("full_name", "Full Name", { placeholder: "Your full name" })}
                      <div className="grid grid-cols-2 gap-4">
                        {field("mobile", "Mobile Number", { placeholder: "+91 98765 43210" })}
                        {field("whatsapp", "WhatsApp Number", { placeholder: "Optional" })}
                      </div>
                      {field("email", "Email", { type: "email", placeholder: "you@email.com" })}
                    </>
                  )}
                  {step === 1 && (
                    <>
                      {field("city", "City", { placeholder: "Your city" })}
                      {field("qualification", "Qualification", { placeholder: "e.g. B.Com, B.Tech, 12th" })}
                      {field("skills", "Skills", { placeholder: "e.g. Content writing, Excel, Design" })}
                    </>
                  )}
                  {step === 2 && (
                    <>
                      <div className="space-y-1.5">
                        <Label className="text-sm font-medium text-neutral-300">
                          Upload Resume <span className="text-neutral-500">(PDF/DOC)</span>
                        </Label>
                        <label
                          htmlFor="wfh-resume"
                          className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-white/15 bg-white/[0.02] px-4 py-3.5 transition-colors hover:border-brand hover:bg-brand/5"
                        >
                          <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-brand/10 text-brand">
                            <Upload className="h-4 w-4" />
                          </span>
                          <span className="truncate text-sm text-neutral-400">
                            {resume ? resume.name : "Click to upload your resume"}
                          </span>
                          <input
                            id="wfh-resume"
                            data-testid={WFH.resume}
                            type="file"
                            accept=".pdf,.doc,.docx"
                            className="hidden"
                            onChange={(e) => setResume(e.target.files?.[0] || null)}
                          />
                        </label>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="wfh-message" className="text-sm font-medium text-neutral-300">
                          Message
                        </Label>
                        <Textarea
                          id="wfh-message"
                          data-testid={WFH.field("message")}
                          value={form.message}
                          onChange={set("message")}
                          rows={3}
                          placeholder="Tell us why you'd be a great fit"
                          className="rounded-xl border-white/10 focus-visible:ring-2 focus-visible:ring-brand"
                        />
                      </div>
                      {serverError && (
                        <p className="text-sm font-medium text-red-500">{serverError}</p>
                      )}
                    </>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <button
                type="button"
                data-testid={WFH.back}
                onClick={back}
                disabled={step === 0}
                className="inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold text-neutral-400 transition-colors hover:text-white disabled:pointer-events-none disabled:opacity-0"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>

              {step < STEPS.length - 1 ? (
                <button
                  type="button"
                  data-testid={WFH.next}
                  onClick={next}
                  className="group inline-flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 text-sm font-semibold text-black shadow-[0_4px_14px_0_rgb(212,175,55,0.39)] transition-transform hover:-translate-y-0.5"
                >
                  Continue
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>
              ) : (
                <button
                  type="button"
                  data-testid={WFH.submit}
                  onClick={submit}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 text-sm font-semibold text-black shadow-[0_4px_14px_0_rgb(212,175,55,0.39)] transition-transform hover:-translate-y-0.5 disabled:opacity-70"
                >
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>
                  ) : (
                    <>Submit application <Check className="h-4 w-4" /></>
                  )}
                </button>
              )}
            </div>
          </div>
        ) : (
          <motion.div
            data-testid={WFH.success}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, ease: easeOut }}
            className="flex flex-col items-center px-8 py-14 text-center"
          >
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-brand/10 text-brand">
              <PartyPopper className="h-8 w-8" />
            </div>
            <h3 className="mt-6 font-display text-2xl font-black tracking-tight text-white">
              Application received!
            </h3>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-neutral-400">
              Thanks {form.full_name.split(" ")[0] || "there"}! We've received your
              details and will get back to you about remote opportunities soon.
            </p>
            <button
              onClick={() => handleOpenChange(false)}
              className="mt-8 rounded-full bg-neutral-900 px-7 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
            >
              Done
            </button>
          </motion.div>
        )}
      </DialogContent>
    </Dialog>
  );
}
